import React, { useEffect, useRef, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { AsesoraTablero, EquipoTablero, Tablero } from "../../../services/v2/prospects.service";
import { dinero, errorTexto } from "./salesHelpers";
import TableroVentas, { BitacoraVentas } from "./tableroVentas";
import TableroPromociones from "./tableroPromociones";
import TableroMetas from "./tableroMetas";
import { tocar } from "../../../festejo/sonidos";
import "./tableroPodio.css";

interface Props { campusId: number; onSalir: () => void; }

const mesesAtras = (n: number): { v: string; t: string }[] => {
	const out = [];
	const d = new Date();
	for (let i = 0; i < n; i++) {
		const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
		const v = m.getFullYear() + "-" + String(m.getMonth() + 1).padStart(2, "0");
		out.push({ v, t: m.toLocaleDateString("es-MX", { month: "long", year: "numeric" }) });
	}
	return out;
};

// El histórico de ventas empieza en mayo 2025 (informes hechos a mano).
const MESES_DESDE_MAYO_2025 = (() => { const d = new Date(); return (d.getFullYear() - 2025) * 12 + d.getMonth() - 4 + 1; })();
// Se vuelve a pedir solo cada tanto: el tablero vive abierto en pantallas del equipo.
const REFRESCO_MS = 3 * 60 * 1000;

const num = (v: number) => Number(v).toLocaleString("es-MX", { maximumFractionDigits: 1 });
const PAIS_NOMBRE: { [k: string]: string } = { hn: "Honduras", mx: "México" };
const iniciales = (n: string) => n.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
const corto = (n: string) => { const t = n.replace(/\s+TRC$/i, "").split(/\s+/).filter(Boolean); return t.length >= 3 ? t[0] + " " + t[2] : t.join(" "); };

/** Admisiones MTY/TRC no tiene logo todavía: escudo con la sigla del plantel, al estilo de los demás. */
export const escudoEquipo = (equipo: string, color: string): string => {
	const sigla = equipo.replace(/^Admisiones\s*/i, "") || "ADM";
	const rayos = [0, 30, 60, 90, 120, 150].map((a) => { const r = a * Math.PI / 180;
		return `<line x1="${50 + Math.cos(r) * 22}" y1="${48 + Math.sin(r) * 22}" x2="${50 + Math.cos(r) * 46}" y2="${48 + Math.sin(r) * 46}"/><line x1="${50 - Math.cos(r) * 22}" y1="${48 - Math.sin(r) * 22}" x2="${50 - Math.cos(r) * 46}" y2="${48 - Math.sin(r) * 46}"/>`; }).join("");
	return "data:image/svg+xml;utf8," + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
	<defs><radialGradient id="g" cx="50%" cy="38%" r="62%"><stop offset="0" stop-color="${color}"/><stop offset=".55" stop-color="${color}" stop-opacity=".55"/><stop offset="1" stop-color="#0B1220"/></radialGradient>
	<linearGradient id="s" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#ffffff"/><stop offset=".5" stop-color="#E5E7EB"/><stop offset="1" stop-color="${color}"/></linearGradient></defs>
	<circle cx="50" cy="50" r="50" fill="url(#g)"/><g stroke="#fff" stroke-opacity=".14" stroke-width="2.5" stroke-linecap="round">${rayos}</g>
	<path d="M50 11 L80 22 V50 C80 67 66 79 50 87 C34 79 20 67 20 50 V22 Z" fill="#0B1220" stroke="#ffffff" stroke-width="2.5" stroke-linejoin="round"/>
	<path d="M50 18 L73 27 V50 C73 62 62 71 50 78 C38 71 27 62 27 50 V27 Z" fill="url(#s)"/>
	<polygon points="50,27 54.6,38.2 66.8,38.8 57.4,46.6 60.4,58.4 50,52 39.6,58.4 42.6,46.6 33.2,38.8 45.4,38.2" fill="#0B1220"/>
	<text x="50" y="71" text-anchor="middle" font-family="Bricolage, Arial Narrow, Arial, sans-serif" font-weight="800" font-size="12" fill="#0B1220">${sigla}</text>
	<path d="M14 82 L86 82 L90 93 L10 93 Z" fill="#0B1220" stroke="#ffffff" stroke-width="1.5" stroke-linejoin="round"/>
	<text x="50" y="90.2" text-anchor="middle" font-family="Bricolage, Arial Narrow, Arial, sans-serif" font-weight="800" font-size="8.2" letter-spacing="1.4" fill="#ffffff">ADMISIONES</text></svg>`);
};
export const logoDe = (e: { equipo: string; logo: string | null; color: string | null }): string => e.logo || escudoEquipo(e.equipo, e.color || "#8A736A");
const colorDe = (e: { color: string | null }): string => e.color || "var(--wa-apagado)";

const Foto: React.FC<{ a: AsesoraTablero }> = ({ a }) => a.foto ? <img src={a.foto} alt="" /> : <span className="pd-ini">{iniciales(a.asesora)}</span>;

/**
 * Tablero de Ventas (Esteban, 2-3 nov 2026): podio con TODOS los equipos del alcance (empate lo gana el monto), carrera a la
 * meta de cada equipo, top 5 de asesoras con foto y ritmo del mes. Lo que entró por solicitud de matrícula aprobada (VentasConteo);
 * el dinero es lo cobrado de cada venta o lo que reportó la asesora mientras Caja postea.
 */
const TableroView: React.FC<Props> = ({ campusId, onSalir }) => {
	const http = HttpManager.getInstance();
	const [mes, setMes] = useState<string>(mesesAtras(1)[0].v);
	// Los equipos de ventas venden para todos los planteles del país (Honduras incluye Online): por eso arranca en "Todo el país".
	const [scope, setScope] = useState<"plantel" | "pais" | "empresa">("pais");
	const [t, setT] = useState<Tablero | null>(null);
	const [error, setError] = useState<string | null>(null);
	// Sube al corregir una venta o con el refresco: vuelve a pedir el tablero sin borrar lo que se ve.
	const [vuelta, setVuelta] = useState(0);
	const [vista, setVista] = useState<"resumen" | "ventas" | "cambios" | "promociones" | "metas">("resumen");

	// Otro mes o alcance: se limpia la pantalla. Una corrección (vuelta) solo refresca, sin parpadeo.
	const ultimaClave = useRef("");
	useEffect(() => {
		const clave = campusId + "|" + mes + "|" + scope;
		if (ultimaClave.current !== clave) {
			ultimaClave.current = clave;
			setT(null);
		}
		setError(null);
		// Si cambian de mes antes de que llegue la respuesta, la vieja ya no se pinta.
		let vigente = true;
		http.prospectsService.dashboard(campusId, mes, scope === "plantel" ? null : scope)
			.then((r) => { if (vigente) setT(r.data); }).catch((e) => { if (vigente) setError(errorTexto(e)); });
		return () => { vigente = false; };
	}, [campusId, mes, scope, vuelta]);
	useEffect(() => {
		const id = window.setInterval(() => { if (document.visibilityState === "visible") setVuelta((n) => n + 1); }, REFRESCO_MS);
		return () => window.clearInterval(id);
	}, []);

	const compiten = (t?.equipos || []).filter((e) => e.compite);
	const simbolo = (p: string) => (p === "mx" ? "$" : "L");
	const montoTexto = t ? Object.entries(t.montos).filter(([, v]) => v > 0 || Object.keys(t.montos).length === 1).map(([p, v]) => dinero(v, simbolo(p))).join(" + ") : "";

	return (
		<div className="tb">
			<div className="tb-cab">
				<div>
					<h2>Tablero de Ventas</h2>
					<span className="apagado">{t ? t.mesTexto : ""}{scope === "pais" ? " · todo el país" : scope === "empresa" ? " · toda la empresa" : ""}</span>
				</div>
				<div className="tb-herr">
					<select value={scope} onChange={(e) => setScope(e.target.value as any)} aria-label="Alcance">
						<option value="pais">Todo el país</option>
						<option value="plantel">Este plantel</option>
						<option value="empresa">Toda la empresa</option>
					</select>
					<select value={mes} onChange={(e) => setMes(e.target.value)} aria-label="Mes">
						{mesesAtras(Math.max(12, MESES_DESDE_MAYO_2025)).map((m) => <option key={m.v} value={m.v}>{m.t}</option>)}
					</select>
					{!t || t.veLista !== false ? <button className="enlace" onClick={onSalir}>Volver a la bandeja</button> : null}
				</div>
			</div>
			<div className="tb-tabs" role="tablist">
				<button role="tab" aria-selected={vista === "resumen"} className={vista === "resumen" ? "activa" : ""} onClick={() => setVista("resumen")}>Resumen</button>
				{/* Ventas del mes y Cambios traen nombres de clientes: solo Supervisión y Dirección (veLista). */}
				{!t || t.veLista !== false ? (
					<>
						<button role="tab" aria-selected={vista === "ventas"} className={vista === "ventas" ? "activa" : ""} onClick={() => setVista("ventas")}>
							{t?.soloMias ? "Mis ventas" : "Ventas del mes"}{t?.ventas ? <span className="cnt">{t.ventas.length}</span> : null}
						</button>
						<button role="tab" aria-selected={vista === "cambios"} className={vista === "cambios" ? "activa" : ""} onClick={() => setVista("cambios")}>
							Cambios{t?.bitacora ? <span className="cnt">{t.bitacora.length}</span> : null}
						</button>
					</>
				) : null}
				<button role="tab" aria-selected={vista === "promociones"} className={vista === "promociones" ? "activa" : ""} onClick={() => setVista("promociones")}>Promociones</button>
				<button role="tab" aria-selected={vista === "metas"} className={vista === "metas" ? "activa" : ""} onClick={() => setVista("metas")}>Metas</button>
			</div>
			{error ? <div className="aviso error">{error}</div> : null}
			{vista === "metas" ? (
				<div className="tb-cuerpo"><TableroMetas campusId={campusId} onGuardado={() => setVuelta((n) => n + 1)} /></div>
			) : !t ? <div className="ventas-vacio">Cargando…</div> : vista === "promociones" ? (
				<div className="tb-cuerpo"><TableroPromociones campusId={campusId} month={mes} scope={scope === "plantel" ? "campus" : "country"} planteles={t.planteles} equipos={t.equipos} /></div>
			) : !t ? <div className="ventas-vacio">Cargando…</div> : vista === "cambios" ? (
				<div className="tb-cuerpo"><BitacoraVentas ventas={t.ventas || []} bitacora={t.bitacora || []} /></div>
			) : vista === "ventas" ? (
				<div className="tb-cuerpo">
					<TableroVentas campusId={campusId} ventas={t.ventas || []} bitacora={t.bitacora || []} equipos={t.equipos} puedeEditar={t.puedeEditar} mesCerrado={!!t.mesCerrado}
						onCambio={() => setVuelta((n) => n + 1)} deInforme={t.fuente === "informe"} soloMias={!!t.soloMias} />
				</div>
			) : (
				<div className="tb-cuerpo pd">
					{t.fuente && t.fuente !== "wapp" ? (
						<div className="aviso">{t.fuente === "informe"
							? "Según el informe de ventas hecho a mano (todo el país): antes del 24 de septiembre de 2026 no había solicitudes en WAPP."
							: "Del 1 al 23 según el informe de ventas hecho a mano (todo el país); desde el 24 las solicitudes de WAPP."}</div>
					) : null}
					{/* Sin notas al pie (Esteban, 2026-11-04: "borrar las observaciones"): solo dato. */}
					<div className="pd-kpis">
						<div className="pd-kpi"><span>Matrículas</span><b className="pd-num">{num(t.matriculas)}</b></div>
						<div className="pd-kpi"><span>{t.fuente === "informe" ? "Monto (informe)" : "Monto cobrado"}</span><b className="pd-num" style={{ fontSize: montoTexto.length > 14 ? 20 : 26 }}>{montoTexto}</b></div>
						<div className="pd-kpi"><span>Equipos en meta</span><b className={"pd-num" + (t.equiposEnMeta ? " ok" : "")}>{t.equiposEnMeta} <small>de {compiten.length}</small></b></div>
						<div className="pd-kpi"><span>Faltan para la meta</span><b className={"pd-num " + (t.faltaMeta ? "oro" : "ok")}>{num(t.faltaMeta)}</b></div>
						{t.sinAsesora ? <div className="pd-kpi"><span>Sin asesora</span><b className="pd-num oro">{num(t.sinAsesora)}</b></div> : null}
					</div>

					<Podio equipos={compiten} empresa={scope === "empresa"} />
					<Carrera equipos={compiten} empresa={scope === "empresa"} enMeta={t.equiposEnMeta} />
					<Top5 asesoras={t.asesoras} equipos={t.equipos} empresa={scope === "empresa"} />
					<Ritmo t={t} />
					<div className="pd-pie">
						Sonidos: clips elegidos por Dirección (Mixkit) · «Screaming Hawk» de Mike Koenig, SoundBible, CC BY 3.0.
						{" "}{compiten.filter((e) => e.sonido).map((e) => <button key={e.equipo} type="button" onClick={() => tocar(e.sonido || "")}>{e.equipo}</button>).reduce<React.ReactNode[]>((acc, b, i) => (i ? [...acc, " · ", b] : [b]), [])}
					</div>
				</div>
			)}
		</div>
	);
};

const metasTexto = (eq: EquipoTablero[]): string => {
	const m: { [k: string]: Set<string> } = {};
	eq.forEach((e) => { const k = num(e.meta); m[k] = m[k] || new Set(); m[k].add(e.equipo.startsWith("Admisiones") ? e.equipo : "ventas"); });
	return "meta " + Object.entries(m).sort((a, b) => Number(b[0]) - Number(a[0])).map(([k, v]) => `${k} ${Array.from(v).join(" y ")}`).join(" · ");
};

const Podio: React.FC<{ equipos: EquipoTablero[]; empresa: boolean }> = ({ equipos, empresa }) => (
	<section className="pd-sec">
		<header><h3>Podio de equipos</h3><span className="sub">{equipos[0] ? `${equipos[0].equipo} lidera con ${num(equipos[0].mat)} matrículas` : ""}</span></header>
		<div className={"pd-podio" + (equipos.length > 6 ? " pd-muchos" : "")} style={{ "--n": String(Math.max(3, equipos.length)) } as React.CSSProperties}>
			{equipos.map((e, i) => (
				<div key={e.pais + e.equipo} className={"pd-peana p" + (i + 1)} style={{ "--col": colorDe(e) } as React.CSSProperties}>
					{i === 0 ? <span className="pd-corona" aria-hidden="true">👑</span> : null}
					<img className="pd-logo" src={logoDe(e)} alt="" />
					<div className="pd-eq">{e.equipo}{empresa ? <small> {PAIS_NOMBRE[e.pais] || e.pais.toUpperCase()}</small> : null}</div>
					<div className="pd-mat pd-num">{num(e.mat)} <small>mat.</small></div>
					<div className="pd-monto pd-num">{dinero(e.monto, e.moneda)}</div>
					<div className="pd-base">{i + 1}</div>
				</div>
			))}
		</div>
	</section>
);

const Carrera: React.FC<{ equipos: EquipoTablero[]; empresa: boolean; enMeta: number }> = ({ equipos, empresa, enMeta }) => {
	const escala = Math.max(1, ...equipos.map((e) => Math.max(e.meta, e.mat))) * 1.08;
	return (
		<section className="pd-sec">
			<header><h3>Carrera a la meta</h3><span className="sub">{metasTexto(equipos).replace(/^meta /, "Meta ")} · {enMeta} {enMeta === 1 ? "ya llegó" : "ya llegaron"}</span></header>
			<div className="pd-carrera">
				{equipos.map((e, i) => {
					const pct = Math.min(100, 100 * e.mat / escala);
					return (
						<div key={e.pais + e.equipo} className={"pd-carril r" + (i + 1)} style={{ "--col": colorDe(e) } as React.CSSProperties}>
							<div className="pd-pos pd-num">{i + 1}</div>
							<div className="pd-pista">
								<div className="pd-relleno" style={{ width: pct + "%" }} />
								{/* Con poco avance el nombre va a la derecha del corredor, para no taparlo. */}
								<span className="pd-nombre" style={pct < 28 ? { left: `calc(${Math.max(pct, 4)}% + 26px)` } : undefined}>{e.equipo}{empresa ? " · " + (PAIS_NOMBRE[e.pais] || e.pais) : ""}</span>
								<div className="pd-meta" style={{ left: (100 * e.meta / escala) + "%" }} title={"Meta " + num(e.meta)} />
								<img className="pd-corredor" src={logoDe(e)} alt="" style={{ left: Math.max(pct, 4) + "%" }} />
							</div>
							<div className="pd-cifras"><b className="pd-num">{num(e.mat)}</b><span className="pd-num">{dinero(e.monto, e.moneda)}</span><br />
								<span className={"pd-falta" + (e.falta ? "" : " ok")}>{e.falta ? "faltan " + num(e.falta) : "¡Meta! +" + num(e.mat - e.meta)}</span></div>
						</div>
					);
				})}
			</div>
		</section>
	);
};

const Top5: React.FC<{ asesoras: AsesoraTablero[]; equipos: EquipoTablero[]; empresa: boolean }> = ({ asesoras, equipos, empresa }) => {
	// Todas las asesoras, plegadas (Esteban, 2026-11-04: "expandible la lista de todas para que vean dónde están").
	const [todas, setTodas] = useState(false);
	const equipoDe = (a: AsesoraTablero) => equipos.find((e) => e.equipo === a.equipo && e.pais === a.pais) || { equipo: a.equipo, logo: null, color: null };
	const moneda = (a: AsesoraTablero) => (a.pais === "mx" ? "$" : "L");
	return (
		<section className="pd-sec">
			<header><h3>Top 5 asesoras</h3><span className="sub">{asesoras[0] ? `${corto(asesoras[0].asesora)} va primera con ${num(asesoras[0].mat)}` : "Sin ventas con asesora"}</span></header>
			{asesoras.length ? (
				<>
					<div className="pd-top5">
						{asesoras.slice(0, 5).map((a, i) => {
							const e = equipoDe(a);
							return (
								<div key={a.pais + a.asesora} className={"pd-ases a" + (i + 1)} style={{ "--col": colorDe(e) } as React.CSSProperties}>
									<div className="pd-foto">{i === 0 ? <span className="pd-aro" aria-hidden="true" /> : null}<Foto a={a} /><span className="pd-n">{i + 1}</span></div>
									<div className="pd-nom">{corto(a.asesora)}</div>
									<div className="pd-chip"><img src={logoDe(e)} alt="" />{a.equipo}{empresa ? " · " + (PAIS_NOMBRE[a.pais] || a.pais) : ""}</div>
									<div className="pd-m pd-num">{num(a.mat)} <small>mat.</small></div>
									<div className="pd-mo pd-num">{dinero(a.monto, moneda(a))}</div>
								</div>
							);
						})}
					</div>
					{asesoras.length > 5 ? (
						<div className="pd-resto">
							{asesoras.slice(5, todas ? asesoras.length : 11).map((a, i) => {
								const e = equipoDe(a);
								return (
									<div key={a.pais + a.asesora} className="pd-fila" style={{ "--col": colorDe(e) } as React.CSSProperties}>
										<span className="pd-pos pd-num">{i + 6}</span><Foto a={a} />
										<span className="pd-nom">{corto(a.asesora)}<small>{a.equipo}{empresa ? " · " + (PAIS_NOMBRE[a.pais] || a.pais) : ""} · {dinero(a.monto, moneda(a))}</small></span>
										<b className="pd-num">{num(a.mat)}</b>
									</div>
								);
							})}
						</div>
					) : null}
					{asesoras.length > 11 ? (
						<button type="button" className="btn btn-outline-primary btn-sm rounded-pill pd-todas" onClick={() => setTodas(!todas)} aria-expanded={todas}>
							{todas ? "Ver menos" : `Ver las ${asesoras.length} asesoras`}
						</button>
					) : null}
				</>
			) : null}
		</section>
	);
};

const Ritmo: React.FC<{ t: Tablero }> = ({ t }) => {
	const ultimo = t.ultimoDia || 30;
	const hoy = t.diaHoy || ultimo;
	const vals = Array.from({ length: ultimo }, (_, i) => t.dias.find((d) => d.dia === i + 1)?.mat || 0);
	const max = Math.max(1, ...vals);
	const total = vals.reduce((s, v) => s + v, 0);
	const prom = total / Math.max(1, hoy);
	const proy = Math.round(prom * ultimo);
	const mejor = vals.indexOf(Math.max(...vals)) + 1;
	const W = 620, H = 150, pad = 18, bw = (W - pad * 2) / ultimo;
	const hoyX = pad + hoy * bw;
	return (
		<section className="pd-sec">
			<header><h3>Ritmo del mes</h3><span className="sub">{t.diaHoy ? `Día ${hoy} de ${ultimo}` : "Mes cerrado"}</span></header>
			<div className="pd-ritmo">
				<svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Matrículas por día">
					{vals.map((v, i) => {
						const h = Math.round((H - 34) * v / max); const x = pad + i * bw; const fut = i + 1 > hoy;
						return (
							<g key={i}>
								<rect x={x + 1} y={H - 22 - h} width={Math.max(1, bw - 2)} height={h} rx={2} fill={fut ? "var(--wa-filo)" : i + 1 === mejor ? "var(--wa-oro)" : "var(--wa-acento)"} opacity={fut ? .6 : .9}><title>{`${i + 1}: ${num(v)}`}</title></rect>
								{(i + 1) % 5 === 0 || i === 0 ? <text x={x + bw / 2} y={H - 7} textAnchor="middle" fontSize={10} fill="var(--wa-apagado)" fontFamily="Hanken, sans-serif">{i + 1}</text> : null}
							</g>
						);
					})}
					{t.diaHoy && hoy < ultimo ? <><line x1={hoyX} x2={hoyX} y1={8} y2={H - 20} stroke="var(--wa-tinta)" strokeDasharray="3 3" opacity={.5} /><text x={hoyX + 4} y={16} fontSize={10} fill="var(--wa-tinta)" fontFamily="Hanken, sans-serif">hoy</text></> : null}
					{total > 0 ? <text x={pad + (mejor - 1) * bw + bw / 2} y={H - 26 - Math.round((H - 34) * vals[mejor - 1] / max)} textAnchor="middle" fontSize={11} fontWeight={800} fill="var(--wa-oro)" fontFamily="Bricolage, sans-serif">{num(vals[mejor - 1])}</text> : null}
				</svg>
				<div className="pd-lado">
					<div><span>Por día</span><b className="pd-num">{num(Number(prom.toFixed(1)))}</b></div>
					<div><span>{t.diaHoy && hoy < ultimo ? "A este ritmo cierran" : "Cerraron en"}</span><b className="pd-num">{num(t.diaHoy && hoy < ultimo ? proy : total)}</b></div>
					<div><span>Mejor día</span><b className="pd-num">{total > 0 ? `${mejor} · ${num(vals[mejor - 1])}` : "—"}</b></div>
				</div>
			</div>
		</section>
	);
};

export default TableroView;
